import React from "react";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../api/axios";
import { Eye, EyeOff, CheckCircle } from "lucide-react";
import BrandLogo from "../components/shared/BrandLogo";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const locketId = searchParams.get("locketId");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm();
  const [successMessage, setSuccessMessage] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const password = watch("password");

  React.useEffect(() => {
    if (!token || !email || !locketId) {
      setError("Invalid or missing reset link. Please request a new password reset.");
    }
  }, [token, email, locketId]);

  const onSubmit = async (data) => {
    setError(null);
    setSuccessMessage(null);
    try {
      await api.post("/qr/reset-password-verify", {
        email,
        locketId,
        token,
        newPassword: data.password,
      });
      setSuccessMessage("Password updated! You can now log in with your new password.");
      setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to reset password. The link may have expired. Please request a new one."
      );
    }
  };

  const inputClass = (hasError) =>
    `w-full px-3 py-2.5 pr-10 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
      hasError
        ? "border-red-400 focus:ring-red-200"
        : "border-slate-300 focus:ring-red-200 focus:border-red-400"
    }`;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-lg mx-auto px-3 py-2">
          <BrandLogo logoClassName="h-7" />
        </div>
      </div>

      <div className="flex flex-col items-center p-4 pt-8">
        <div className="w-full max-w-sm">
          {locketId && (
            <div className="text-center mb-3">
              <div className="inline-block bg-slate-100 px-4 py-1.5 rounded-lg border border-slate-200">
                <span className="text-xs font-bold text-slate-500">QR CODE ID:</span>{" "}
                <span className="text-sm font-bold text-red-600">{locketId}</span>
              </div>
            </div>
          )}

          <h2 className="text-xl font-extrabold text-slate-900 mb-1 text-center">Create New Password</h2>
          <p className="text-xs text-slate-500 text-center mb-4">
            Pick a strong password to keep your emergency info secure.
          </p>

          {successMessage ? (
            <div className="bg-green-50 border border-green-300 rounded-lg p-5 text-center">
              <CheckCircle size={32} className="text-green-600 mx-auto mb-2" />
              <p className="text-green-700 text-sm font-semibold">{successMessage}</p>
              <p className="text-slate-500 text-xs mt-1">Redirecting to home page...</p>
            </div>
          ) : error && !token ? (
            <div>
              <div className="bg-red-50 border border-red-300 rounded-lg p-5 mb-4 text-center">
                <p className="text-red-700 text-sm font-semibold">{error}</p>
              </div>
              <Link to="/forgot-password">
                <button className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors duration-150">
                  Request New Reset Link
                </button>
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register("password", {
                        required: "Password is required",
                        minLength: { value: 8, message: "Minimum 8 characters" },
                        validate: {
                          hasUpper: (v) => /[A-Z]/.test(v) || "Must have an uppercase letter",
                          hasLower: (v) => /[a-z]/.test(v) || "Must have a lowercase letter",
                          hasNumber: (v) => /[0-9]/.test(v) || "Must have a number",
                        },
                      })}
                      className={inputClass(errors.password)}
                      placeholder="New password (8+ chars)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      {...register("confirmPassword", {
                        required: "Please confirm your password",
                        validate: (v) => v === password || "Passwords do not match",
                      })}
                      className={inputClass(errors.confirmPassword)}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.confirmPassword.message}</p>
                  )}
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                    <p className="text-red-700 text-sm font-semibold">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-lg shadow-sm transition-colors duration-150"
                >
                  {isSubmitting ? "Saving..." : "Save New Password"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/forgot-password"
                  className="text-slate-500 text-sm font-semibold hover:text-slate-700"
                >
                  ← Request a new link instead
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
